// src/pages/packingBatch/CreatePackingBatchPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  Search,
  Filter,
  X,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronDown,
  Truck,
  MapPin,
  Store,
  User,
  Phone,
  CreditCard,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Copy,
  Ban,
  Eye,
  EyeOff,
  Receipt,
} from 'lucide-react';
import {
  getUserWarehouses,
  searchEligibleOrders,
  createPackingBatchWithProgress,
  getOrderPaymentRecords,
} from '../../services/api/packingBatch';
import { getBusinessCouriers } from '../../services/api/businessCourier';
import { getRulesByBusinessCourier } from '../../services/api/courierAssignmentRule';
import { getAllOrderReferenceData, getOrderStatuses } from '../../services/api/orderReference';
import { getShops } from '../../services/api/shops';
import { getProducts } from '../../services/api/productSearch';
import { updateEcomOrder } from '../../services/api/orders';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatCurrency } from '../../utils/orderCalculations';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import MultiSelectFilter from '../../components/common/MultiSelectFilter';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import styles from './CreatePackingBatchPage.module.css';

const CreatePackingBatchPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  // Filter state
  const [warehouses, setWarehouses] = useState([]);
  const [businessCourierAccounts, setBusinessCourierAccounts] = useState([]);
  const [courierRules, setCourierRules] = useState([]);
  const [shops, setShops] = useState([]);
  const [cities, setCities] = useState([]);
  const [orderStatuses, setOrderStatuses] = useState([]);

  // Products for include filter
  const [filterProducts, setFilterProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  // Tracks selected product objects (id → object) so labels survive search replacement
  const [selectedProductObjects, setSelectedProductObjects] = useState({});

  // Products for exclude filter
  const [excludeFilterProducts, setExcludeFilterProducts] = useState([]);
  const [isLoadingExcludeProducts, setIsLoadingExcludeProducts] = useState(false);
  const [selectedExcludeProductObjects, setSelectedExcludeProductObjects] = useState({});

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const [filters, setFilters] = useState({
    warehouseId: '',
    businessCourierId: '',
    courierAssignmentRuleId: '',
    orderStatusKeys: ['confirmed'],
    shopIds: [],
    cityIds: [],
    productIds: [],
    excludeProductIds: [],
    search: ''
  });

  // Orders state
  const [orders, setOrders] = useState([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Duplicate orders view state
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);
  // Track orders being cancelled (duplicate quick-cancel)
  const [cancellingOrderId, setCancellingOrderId] = useState(null);

  // Cancel order with reason modal
  const [cancelReasons, setCancelReasons] = useState([]);
  const [cancelModal, setCancelModal] = useState(null); // { order } or null
  const [cancelModalReason, setCancelModalReason] = useState('');
  const [isCancellingOrder, setIsCancellingOrder] = useState(false);

  // Payment records popup
  const [paymentRecordsCache, setPaymentRecordsCache] = useState({});
  const [paymentRecordsPopup, setPaymentRecordsPopup] = useState(null); // { order, records } or null
  const [loadingPaymentRecordsId, setLoadingPaymentRecordsId] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [pageSize] = useState(1500);

  // Sorting
  const [sortBy, setSortBy] = useState('itemsDisplay');
  const [sortDirection, setSortDirection] = useState('ASC');

  // Batch creation state
  const [notes, setNotes] = useState('');
  const [isCreatingBatch, setIsCreatingBatch] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [progressData, setProgressData] = useState({
    current: 0,
    total: 0,
    orderNumber: '',
    courierAccount: '',
    status: 'processing',
    results: []
  });

  useEffect(() => {
    loadReferenceData();
  }, []);

  useEffect(() => {
    if (filters.businessCourierId) {
      loadCourierRules(filters.businessCourierId);
    } else {
      setCourierRules([]);
      setFilters(prev => ({ ...prev, courierAssignmentRuleId: '' }));
    }
  }, [filters.businessCourierId]);

  const loadReferenceData = async () => {
    try {
      const [warehousesRes, couriersRes, referenceRes, shopsRes] = await Promise.all([
        getUserWarehouses(),
        getBusinessCouriers({ activeOnly: true }),
        getAllOrderReferenceData(),
        getShops()
      ]);

      const warehouseList = warehousesRes.warehouses || [];
      const courierList = couriersRes.couriers || [];

      setWarehouses(warehouseList);
      setBusinessCourierAccounts(courierList);
      setShops(shopsRes || []);
      setCities(referenceRes.cities || []);

      const eligibleStatuses = (referenceRes.orderStatuses || []).filter(
        s => !s.isInPacking && !s.isDispatched && !s.isClosed
      );
      setOrderStatuses(eligibleStatuses);
      setCancelReasons(referenceRes.orderStatusReasons?.['cancelled'] || []);

      if (warehouseList.length === 1) {
        setFilters(prev => ({ ...prev, warehouseId: warehouseList[0].id.toString() }));
      }
      if (courierList.length === 1) {
        setFilters(prev => ({ ...prev, businessCourierId: courierList[0].id.toString() }));
      }
    } catch (error) {
      console.error('Failed to load reference data:', error);
      showError(t('common.loadError'));
    }
  };

  const loadCourierRules = async (businessCourierId) => {
    try {
      const response = await getRulesByBusinessCourier(businessCourierId);
      const rulesList = response.rules || [];
      setCourierRules(rulesList);
      const defaultRule = rulesList.find(rule => rule.isDefault);
      if (defaultRule) {
        setFilters(prev => ({ ...prev, courierAssignmentRuleId: defaultRule.id.toString() }));
      }
    } catch (error) {
      console.error('Failed to load courier rules:', error);
      setCourierRules([]);
    }
  };

  // ============================================================
  // INCLUDE PRODUCTS — async search
  // Keep a map of selected objects so they survive option replacement
  // ============================================================
  const handleProductSearch = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) return;
    try {
      setIsLoadingProducts(true);
      const response = await getProducts({ search: searchTerm, limit: 50, page: 1 });
      const productList = (response.products || []).filter(p => p.type !== 'bundle').map(p => ({
        id: p.variationId ? `p${p.productId}-v${p.variationId}` : `p${p.productId}`,
        productId: p.productId,
        variationId: p.variationId || null,
        name: p.displayName || p.name,
        sku: p.sku || ''
      }));
      setFilterProducts(productList);
    } catch (error) {
      console.error('Failed to search products:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const [productQuantities, setProductQuantities] = useState({});

  const handleProductFilterChange = (selectedCompositeIds) => {
    // Build updated map of selected objects, preserving previously selected ones
    const newSelectedMap = { ...selectedProductObjects };

    // Add any newly visible selected items
    filterProducts.forEach(p => {
      if (selectedCompositeIds.includes(p.id)) {
        newSelectedMap[p.id] = p;
      }
    });

    // Remove deselected items
    Object.keys(newSelectedMap).forEach(id => {
      if (!selectedCompositeIds.includes(id)) {
        delete newSelectedMap[id];
      }
    });

    setSelectedProductObjects(newSelectedMap);

    // Extract unique productIds for the filter param
    const productIds = [...new Set(
      Object.values(newSelectedMap).map(p => p.productId)
    )];
    setFilters(prev => ({ ...prev, productIds }));
    setProductQuantities(prev => Object.fromEntries(
      Object.entries(prev).filter(([id]) => productIds.includes(Number(id)))
    ));
  };

  // Merge current search results with already-selected items so the
  // MultiSelectFilter always has the selected options in its list
  const mergedIncludeOptions = useMemo(() => {
    const map = {};
    // Start with already-selected objects so they're always present
    Object.values(selectedProductObjects).forEach(p => { map[p.id] = p; });
    // Add / overwrite with fresh search results
    filterProducts.forEach(p => { map[p.id] = p; });
    return Object.values(map);
  }, [filterProducts, selectedProductObjects]);

  const selectedIncludeCompositeIds = Object.keys(selectedProductObjects);

  // ============================================================
  // EXCLUDE PRODUCTS — same pattern
  // ============================================================
  const handleExcludeProductSearch = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) return;
    try {
      setIsLoadingExcludeProducts(true);
      const response = await getProducts({ search: searchTerm, limit: 50, page: 1 });
      const productList = (response.products || []).filter(p => p.type !== 'bundle').map(p => ({
        id: p.variationId ? `p${p.productId}-v${p.variationId}` : `p${p.productId}`,
        productId: p.productId,
        variationId: p.variationId || null,
        name: p.displayName || p.name,
        sku: p.sku || ''
      }));
      setExcludeFilterProducts(productList);
    } catch (error) {
      console.error('Failed to search exclude products:', error);
    } finally {
      setIsLoadingExcludeProducts(false);
    }
  };

  const handleExcludeProductFilterChange = (selectedCompositeIds) => {
    const newSelectedMap = { ...selectedExcludeProductObjects };

    excludeFilterProducts.forEach(p => {
      if (selectedCompositeIds.includes(p.id)) {
        newSelectedMap[p.id] = p;
      }
    });

    Object.keys(newSelectedMap).forEach(id => {
      if (!selectedCompositeIds.includes(id)) {
        delete newSelectedMap[id];
      }
    });

    setSelectedExcludeProductObjects(newSelectedMap);

    const excludeProductIds = [...new Set(
      Object.values(newSelectedMap).map(p => p.productId)
    )];
    setFilters(prev => ({ ...prev, excludeProductIds }));
  };

  const mergedExcludeOptions = useMemo(() => {
    const map = {};
    Object.values(selectedExcludeProductObjects).forEach(p => { map[p.id] = p; });
    excludeFilterProducts.forEach(p => { map[p.id] = p; });
    return Object.values(map);
  }, [excludeFilterProducts, selectedExcludeProductObjects]);

  const selectedExcludeCompositeIds = Object.keys(selectedExcludeProductObjects);

  // ============================================================
  // GENERAL FILTER HANDLERS
  // ============================================================
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearchOrders = async () => {
    if (!filters.warehouseId) {
      showError(t('packingBatch.selectWarehouseFirst'));
      return;
    }
    if (!filters.businessCourierId) {
      showError(t('packingBatch.selectCourierFirst'));
      return;
    }

    try {
      setIsSearching(true);
      setShowDuplicatesOnly(false);
      const params = {
        warehouseId: filters.warehouseId,
        businessCourierId: filters.businessCourierId,
        orderStatusKeys: filters.orderStatusKeys.join(','),
        channel: 'online',
        page: currentPage,
        limit: pageSize
      };

      if (filters.courierAssignmentRuleId) params.courierAssignmentRuleId = filters.courierAssignmentRuleId;
      if (filters.shopIds.length > 0) params.shopIds = filters.shopIds.join(',');
      if (filters.cityIds.length > 0) params.cityIds = filters.cityIds.join(',');
      if (filters.productIds.length > 0) params.productIds = filters.productIds.join(',');
      const qtyPairs = filters.productIds
        .filter(id => Number(productQuantities[id]) > 0)
        .map(id => `${id}:${Number(productQuantities[id])}`);
      if (qtyPairs.length > 0) params.productQuantities = qtyPairs.join(',');
      if (filters.excludeProductIds.length > 0) params.excludeProductIds = filters.excludeProductIds.join(',');
      if (filters.search) params.search = filters.search;

      const response = await searchEligibleOrders(params);
      const fetchedOrders = response.orders || [];
      setOrders(fetchedOrders);
      setTotalOrders(response.total || 0);
      setHasSearched(true);
      setSelectedOrderIds(fetchedOrders.map(o => o.id));
    } catch (error) {
      console.error('Failed to search orders:', error);
      showError(error.response?.data?.message || t('packingBatch.searchFailed'));
    } finally {
      setIsSearching(false);
    }
  };

  // ============================================================
  // DUPLICATE DETECTION
  // Build a set of phone numbers that appear more than once
  // ============================================================
  const duplicatePhones = useMemo(() => {
    const phoneCounts = {};
    orders.forEach(order => {
      const phone = order.primaryPhone;
      if (phone) {
        phoneCounts[phone] = (phoneCounts[phone] || 0) + 1;
      }
    });
    return new Set(
      Object.entries(phoneCounts)
        .filter(([, count]) => count > 1)
        .map(([phone]) => phone)
    );
  }, [orders]);

  const duplicateOrderCount = useMemo(() => {
    return orders.filter(o => duplicatePhones.has(o.primaryPhone)).length;
  }, [orders, duplicatePhones]);

  // ============================================================
  // CANCEL ORDER (mark as cancelled + duplicate reason)
  // ============================================================
  const handleCancelOrder = async (order) => {
    if (cancellingOrderId) return; // prevent double clicks
    const confirmed = window.confirm(
      `Cancel order #${order.orderNumber}? This will set the status to Cancelled (Duplicate).`
    );
    if (!confirmed) return;

    try {
      setCancellingOrderId(order.id);
      await updateEcomOrder(order.id, {
        orderStatusKey: 'cancelled',
        orderStatusReasonKey: 'duplicate'
      });
      // Remove from local list without reloading
      setOrders(prev => prev.filter(o => o.id !== order.id));
      setSelectedOrderIds(prev => prev.filter(id => id !== order.id));
      success(`Order #${order.orderNumber} cancelled.`);
    } catch (err) {
      console.error('Failed to cancel order:', err);
      showError(err.response?.data?.message || 'Failed to cancel order.');
    } finally {
      setCancellingOrderId(null);
    }
  };

  // ============================================================
  // CANCEL ORDER WITH REASON (general cancel modal)
  // ============================================================
  const canCancelOrder = hasPermission('cancel_ecom_order');

  const handleOpenCancelModal = (order) => {
    setCancelModal({ order });
    setCancelModalReason(cancelReasons.length > 0 ? cancelReasons[0].key : '');
  };

  const handleCancelOrderWithReason = async () => {
    if (!cancelModal || !cancelModalReason) return;
    const { order } = cancelModal;
    try {
      setIsCancellingOrder(true);
      await updateEcomOrder(order.id, {
        orderStatusKey: 'cancelled',
        orderStatusReasonKey: cancelModalReason
      });
      setOrders(prev => prev.filter(o => o.id !== order.id));
      setSelectedOrderIds(prev => prev.filter(id => id !== order.id));
      success(`Order #${order.orderNumber} cancelled.`);
      setCancelModal(null);
    } catch (err) {
      console.error('Failed to cancel order:', err);
      showError(err.response?.data?.message || 'Failed to cancel order.');
    } finally {
      setIsCancellingOrder(false);
    }
  };

  // ============================================================
  // PAYMENT RECORDS POPUP
  // ============================================================
  const handleShowPaymentRecords = async (order) => {
    if (paymentRecordsCache[order.id]) {
      setPaymentRecordsPopup({ order, records: paymentRecordsCache[order.id] });
      return;
    }
    try {
      setLoadingPaymentRecordsId(order.id);
      const data = await getOrderPaymentRecords(order.id);
      const records = data.paymentRecords || [];
      setPaymentRecordsCache(prev => ({ ...prev, [order.id]: records }));
      setPaymentRecordsPopup({ order, records });
    } catch (err) {
      console.error('Failed to load payment records:', err);
      showError('Failed to load payment records.');
    } finally {
      setLoadingPaymentRecordsId(null);
    }
  };

  // ============================================================
  // SORTING + DISPLAY ORDERS
  // ============================================================
  const sortedOrders = useMemo(() => {
    if (!orders.length) return [];

    let list = [...orders];

    // When showing duplicates only, filter and sort by phone
    if (showDuplicatesOnly) {
      list = list.filter(o => duplicatePhones.has(o.primaryPhone));
      // Sort by phone number so duplicates group together
      list.sort((a, b) => (a.primaryPhone || '').localeCompare(b.primaryPhone || ''));
      return list;
    }

    // Normal sort
    return list.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'itemsDisplay':
          aVal = a.itemsDisplay || '';
          bVal = b.itemsDisplay || '';
          break;
        case 'orderNumber':
          aVal = a.orderNumber || '';
          bVal = b.orderNumber || '';
          break;
        case 'grandTotal':
          aVal = parseFloat(a.grandTotal) || 0;
          bVal = parseFloat(b.grandTotal) || 0;
          if (sortDirection === 'ASC') return aVal - bVal;
          return bVal - aVal;
        case 'customerName':
          aVal = a.customerName || '';
          bVal = b.customerName || '';
          break;
        default:
          aVal = a[sortBy] || '';
          bVal = b[sortBy] || '';
      }
      if (typeof aVal === 'string') {
        const cmp = aVal.localeCompare(bVal);
        return sortDirection === 'ASC' ? cmp : -cmp;
      }
      return 0;
    });
  }, [orders, sortBy, sortDirection, showDuplicatesOnly, duplicatePhones]);

  const handleSort = (field) => {
    if (showDuplicatesOnly) return; // no custom sort in duplicates view
    if (sortBy === field) {
      setSortDirection(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(field);
      setSortDirection('ASC');
    }
  };

  const getSortIcon = (field) => {
    if (showDuplicatesOnly) return null;
    if (sortBy !== field) return <ArrowUpDown size={14} className={styles.sortIconInactive} />;
    return sortDirection === 'ASC'
      ? <ArrowUp size={14} className={styles.sortIconActive} />
      : <ArrowDown size={14} className={styles.sortIconActive} />;
  };

  // ============================================================
  // SELECT / DESELECT
  // ============================================================
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrderIds(sortedOrders.map(o => o.id));
    } else {
      setSelectedOrderIds([]);
    }
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrderIds(prev =>
      prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]
    );
  };

  // ============================================================
  // BATCH CREATION
  // ============================================================
  const handleCreateBatch = async () => {
    if (selectedOrderIds.length === 0) {
      showError(t('packingBatch.selectAtLeastOneOrder'));
      return;
    }
    if (selectedOrderIds.length > 100) {
      const confirmed = window.confirm(
        t('packingBatch.largeBatchConfirm', { count: selectedOrderIds.length })
      );
      if (!confirmed) return;
    }

    try {
      setIsCreatingBatch(true);
      setShowProgress(true);
      setProgressData({
        current: 0,
        total: selectedOrderIds.length,
        orderNumber: '',
        courierAccount: '',
        status: 'processing',
        results: []
      });

      const batchData = {
        businessCourierId: parseInt(filters.businessCourierId),
        warehouseId: parseInt(filters.warehouseId),
        orderIds: selectedOrderIds,
        notes,
        channel: 'online'
      };

      await createPackingBatchWithProgress(
        batchData,
        (progressUpdate) => {
          setProgressData(prev => ({
            ...prev,
            current: progressUpdate.current || prev.current,
            total: progressUpdate.total || prev.total,
            orderNumber: progressUpdate.orderNumber || prev.orderNumber,
            courierAccount: progressUpdate.courierAccount || prev.courierAccount,
            status: progressUpdate.status || prev.status,
            results: [...prev.results, progressUpdate]
          }));
        },
        (result) => {
          setShowProgress(false);
          setIsCreatingBatch(false);
          if (result.batchStatus === 'completed') {
            navigate(`/packing-batches/waybill-print?packingBatchId=${result.batchId}`);
          } else if (['partially_failed', 'failed', 'retry_needed'].includes(result.batchStatus)) {
            navigate(`/packing-batches/${result.batchId}`);
          } else {
            success(t('packingBatch.batchCreatedSuccessfully'));
            navigate(`/packing-batches/${result.batchId}`);
          }
        },
        (error) => {
          showError(error.message || t('packingBatch.batchCreationFailed'));
          setShowProgress(false);
          setIsCreatingBatch(false);
        }
      );
    } catch (error) {
      console.error('Failed to create batch:', error);
      showError(error.message || t('packingBatch.batchCreationFailed'));
      setShowProgress(false);
      setIsCreatingBatch(false);
    }
  };

  // ============================================================
  // DERIVED VALUES
  // ============================================================
  const selectedOrdersValue = selectedOrderIds.reduce((sum, id) => {
    const order = orders.find(o => o.id === id);
    return sum + (order ? parseFloat(order.grandTotal) : 0);
  }, 0);

  const totalPages = Math.ceil(totalOrders / pageSize);
  const selectedCourierAccount = businessCourierAccounts.find(
    acc => acc.id.toString() === filters.businessCourierId
  );

  const citiesWithDisplay = cities.map(city => ({
    ...city,
    displayName: city.District ? `${city.name} — ${city.District.name}` : city.name
  }));

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className={styles.createBatchPage}>
      <PageHeader
        title={t('packingBatch.createPackingBatch')}
        description={t('packingBatch.createDescription')}
        icon={Package}
      />

      {/* Primary Filters */}
      <div className={styles.primaryFiltersSection}>
        <div className={styles.primaryFilterGrid}>
          {/* Warehouse */}
          <div className={styles.formGroup}>
            <label>{t('packingBatch.warehouse')} *</label>
            <Dropdown
              value={filters.warehouseId}
              onChange={(v) => handleFilterChange('warehouseId', v != null ? String(v) : '')}
              options={warehouses.map(w => ({ value: String(w.id), label: w.name }))}
              placeholder={t('packingBatch.selectWarehouse')}
              icon={<MapPin size={18} />}
            />
          </div>

          {/* Courier Account */}
          <div className={styles.formGroup}>
            <label>{t('packingBatch.courierAccount')} *</label>
            <Dropdown
              value={filters.businessCourierId}
              onChange={(v) => handleFilterChange('businessCourierId', v != null ? String(v) : '')}
              options={businessCourierAccounts.map(account => ({
                value: String(account.id),
                label: `${account.accountName} (${account.CourierCompany?.name})`,
              }))}
              placeholder={t('packingBatch.selectCourierAccount')}
              icon={<Truck size={18} />}
            />
          </div>

          {/* Courier Assignment Rule */}
          <div className={styles.formGroup}>
            <label>{t('packingBatch.courierRule')}</label>
            <div className={styles.selectWrapper}>
              <Filter className={styles.inputIcon} size={18} />
              <select
                value={filters.courierAssignmentRuleId}
                onChange={(e) => handleFilterChange('courierAssignmentRuleId', e.target.value)}
                className={styles.select}
                disabled={courierRules.length === 0}
              >
                <option value="">{t('packingBatch.allOrders')}</option>
                {courierRules.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
              <ChevronDown className={styles.selectArrow} size={18} />
            </div>
          </div>
        </div>

        <div className={styles.primaryActions}>
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={styles.filterToggleButton}
          >
            <Filter size={18} />
            {showAdvancedFilters ? t('packingBatch.hideAdvanced') : t('packingBatch.showAdvanced')}
            {showAdvancedFilters ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          <button
            onClick={handleSearchOrders}
            disabled={isSearching || !filters.warehouseId || !filters.businessCourierId}
            className={styles.searchButton}
          >
            {isSearching ? (
              <>
                <Loader2 size={18} className={styles.spinning} />
                {t('packingBatch.searching')}
              </>
            ) : (
              <>
                <Search size={18} />
                {t('packingBatch.searchOrders')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Advanced Filters */}
      {showAdvancedFilters && (
        <div className={styles.advancedFiltersSection}>
          <div className={styles.sectionHeader}>
            <h2>
              <Filter size={18} />
              {t('packingBatch.advancedFilters')}
            </h2>
          </div>

          <div className={styles.filterGrid}>
            {/* Order Status */}
            <div className={styles.formGroup}>
              <MultiSelectFilter
                label={t('packingBatch.orderStatus')}
                options={orderStatuses}
                selectedValues={filters.orderStatusKeys}
                onChange={(values) => handleFilterChange('orderStatusKeys', values)}
                placeholder={t('packingBatch.selectStatuses')}
                displayKey="label"
                valueKey="key"
                searchKey="label"
              />
            </div>

            {/* Shop Filter */}
            <div className={styles.formGroup}>
              <MultiSelectFilter
                label={t('packingBatch.shop')}
                options={shops}
                selectedValues={filters.shopIds}
                onChange={(values) => handleFilterChange('shopIds', values)}
                placeholder={t('packingBatch.allShops')}
                displayKey="name"
                valueKey="id"
                searchKey="name"
              />
            </div>

            {/* City Filter */}
            <div className={styles.formGroup}>
              <MultiSelectFilter
                label={t('packingBatch.city')}
                options={citiesWithDisplay}
                selectedValues={filters.cityIds}
                onChange={(values) => handleFilterChange('cityIds', values)}
                placeholder={t('packingBatch.allCities')}
                displayKey="displayName"
                valueKey="id"
                searchKey="displayName"
                searchable
              />
            </div>

            {/* Include Product Filter */}
            <div className={styles.formGroup}>
              <MultiSelectFilter
                label={t('packingBatch.products')}
                options={mergedIncludeOptions}
                selectedValues={selectedIncludeCompositeIds}
                onChange={handleProductFilterChange}
                placeholder={t('packingBatch.allProducts')}
                displayKey="name"
                valueKey="id"
                searchKey="name"
                onSearch={handleProductSearch}
                isLoading={isLoadingProducts}
                renderOptionExtra={(option, isSelected) => isSelected && (
                  <input
                    type="number"
                    min="1"
                    step="1"
                    className={styles.qtyInput}
                    placeholder={t('packingBatch.qtyPlaceholder')}
                    value={productQuantities[option.productId] ?? ''}
                    onChange={(e) => setProductQuantities(prev => ({ ...prev, [option.productId]: e.target.value }))}
                  />
                )}
              />
            </div>

            {/* Exclude Product Filter */}
            <div className={styles.formGroup}>
              <MultiSelectFilter
                label="Exclude Products"
                options={mergedExcludeOptions}
                selectedValues={selectedExcludeCompositeIds}
                onChange={handleExcludeProductFilterChange}
                placeholder="Exclude orders with these products..."
                displayKey="name"
                valueKey="id"
                searchKey="name"
                onSearch={handleExcludeProductSearch}
                isLoading={isLoadingExcludeProducts}
              />
            </div>

            {/* Search */}
            <div className={styles.formGroup}>
              <label>{t('common.search')}</label>
              <div className={styles.inputWrapper}>
                <Search className={styles.inputIcon} size={18} />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder={t('packingBatch.searchPlaceholder')}
                  className={styles.input}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchOrders()}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compact Batch Panel */}
      {hasSearched && selectedOrderIds.length > 0 && (
        <div className={styles.compactBatchPanel}>
          <div className={styles.compactPanelContent}>
            <div className={styles.compactSummary}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>{t('packingBatch.account')}:</span>
                <span className={styles.summaryValue}>
                  {selectedCourierAccount?.accountName || t('common.na')}
                </span>
              </div>
              <div className={styles.summaryDivider}>|</div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>{t('packingBatch.selected')}:</span>
                <span className={styles.summaryValue}>
                  {t('packingBatch.ordersCount', { count: selectedOrderIds.length })}
                </span>
              </div>
              <div className={styles.summaryDivider}>|</div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>{t('packingBatch.totalValue')}:</span>
                <span className={styles.summaryValue}>{formatCurrency(selectedOrdersValue)}</span>
              </div>
            </div>

            <div className={styles.compactActions}>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('packingBatch.notesPlaceholder')}
                className={styles.compactInput}
              />
              <button
                onClick={() => setSelectedOrderIds([])}
                className={styles.compactClearButton}
                title={t('packingBatch.clearSelection')}
              >
                <X size={16} />
              </button>
              <button
                onClick={handleCreateBatch}
                disabled={isCreatingBatch}
                className={styles.compactCreateButton}
              >
                {isCreatingBatch ? (
                  <>
                    <Loader2 size={18} className={styles.spinning} />
                    {t('packingBatch.creating')}
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    {t('packingBatch.createBatch')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Orders Table */}
      {hasSearched && (
        <div className={styles.ordersSection}>
          <div className={styles.sectionHeader}>
            <h2>
              <Package size={18} />
              {t('packingBatch.eligibleOrders', { count: totalOrders })}
            </h2>

            {/* Duplicate orders toggle */}
            {duplicateOrderCount > 0 && (
              <div className={styles.duplicateToggleArea}>
                <span className={styles.duplicateAlert}>
                  <AlertCircle size={14} />
                  {duplicateOrderCount} orders with duplicate phone numbers
                </span>
                <button
                  className={`${styles.duplicateToggleButton} ${showDuplicatesOnly ? styles.duplicateToggleActive : ''}`}
                  onClick={() => setShowDuplicatesOnly(prev => !prev)}
                >
                  {showDuplicatesOnly ? (
                    <><EyeOff size={14} /> Show All Orders</>
                  ) : (
                    <><Eye size={14} /> Show Duplicates Only</>
                  )}
                </button>
              </div>
            )}
          </div>

          {sortedOrders.length > 0 ? (
            <>
              <div className={styles.tableContainer}>
                <table className={styles.ordersTable}>
                  <thead>
                    <tr>
                      <th className={styles.checkboxColumn}>
                        <input
                          type="checkbox"
                          checked={
                            selectedOrderIds.length === sortedOrders.length &&
                            sortedOrders.length > 0
                          }
                          onChange={handleSelectAll}
                          className={styles.checkbox}
                        />
                      </th>
                      <th
                        onClick={() => handleSort('orderNumber')}
                        className={styles.sortableHeader}
                      >
                        {t('packingBatch.order')} {getSortIcon('orderNumber')}
                      </th>
                      <th
                        onClick={() => handleSort('customerName')}
                        className={styles.sortableHeader}
                      >
                        {t('packingBatch.customer')} {getSortIcon('customerName')}
                      </th>
                      <th>{t('packingBatch.shop')}</th>
                      <th>{t('packingBatch.city')}</th>
                      <th>{t('packingBatch.payment')}</th>
                      <th>{t('packingBatch.status')}</th>
                      <th
                        onClick={() => handleSort('grandTotal')}
                        className={styles.sortableHeader}
                      >
                        {t('packingBatch.total')} {getSortIcon('grandTotal')}
                      </th>
                      <th
                        onClick={() => handleSort('itemsDisplay')}
                        className={styles.sortableHeader}
                      >
                        {t('packingBatch.items')} {getSortIcon('itemsDisplay')}
                      </th>
                      <th className={styles.actionsColumn}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedOrders.map((order) => {
                      const isDuplicate = duplicatePhones.has(order.primaryPhone);
                      return (
                        <tr
                          key={order.id}
                          className={`${styles.orderRow} ${isDuplicate ? styles.duplicateRow : ''}`}
                        >
                          <td className={styles.checkboxColumn}>
                            <input
                              type="checkbox"
                              checked={selectedOrderIds.includes(order.id)}
                              onChange={() => handleSelectOrder(order.id)}
                              className={styles.checkbox}
                            />
                          </td>
                          <td>
                            <div className={styles.orderNumber}>
                              #{order.orderNumber}
                            </div>
                          </td>
                          <td>
                            <div className={styles.customerInfo}>
                              <div className={styles.customerName}>
                                <User size={12} />
                                {order.customerName}
                              </div>
                              {order.primaryPhone && (
                                <div className={`${styles.customerPhone} ${isDuplicate ? styles.duplicatePhone : ''}`}>
                                  <Phone size={12} />
                                  {order.primaryPhone}
                                  {isDuplicate && (
                                    <span className={styles.duplicatePhoneBadge}>
                                      DUPLICATE
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className={styles.shopBadge}>
                              <Store size={12} />
                              {order.Shop?.name || t('common.na')}
                            </div>
                          </td>
                          <td>
                            <div className={styles.cityName}>
                              <MapPin size={12} />
                              {order.City?.name || t('common.na')}
                            </div>
                          </td>
                          <td>
                            {(() => {
                              const isCod = order.PaymentType?.key?.toLowerCase() === 'cash_on_delivery';
                              return (
                                <div className={styles.paymentCell}>
                                  <div className={isCod ? styles.paymentBadge : styles.nonCodPaymentBadge}>
                                    <CreditCard size={12} />
                                    {order.PaymentType?.label || t('common.na')}
                                  </div>
                                  {!isCod && (
                                    <button
                                      className={styles.paymentRecordsBtn}
                                      title="View payment records"
                                      onClick={() => handleShowPaymentRecords(order)}
                                      disabled={loadingPaymentRecordsId === order.id}
                                    >
                                      {loadingPaymentRecordsId === order.id
                                        ? <Loader2 size={12} className={styles.spinning} />
                                        : <Receipt size={12} />
                                      }
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td>
                            <div
                              className={styles.statusBadge}
                              style={{
                                backgroundColor: order.OrderStatus?.colorCode
                                  ? `${order.OrderStatus.colorCode}20`
                                  : '#F59E0B20',
                                borderColor: order.OrderStatus?.colorCode || '#F59E0B',
                                color: order.OrderStatus?.colorCode || '#92400e'
                              }}
                            >
                              {order.OrderStatus?.label || order.orderStatusKey}
                            </div>
                          </td>
                          <td>
                            <div className={styles.totalAmount}>
                              {formatCurrency(order.grandTotal)}
                            </div>
                          </td>
                          <td>
                            <div className={styles.productsInfo}>
                              {(order.orderItems || []).map((item, idx) => {
                                const productName = item.Product?.name || 'Unknown';
                                const attrDisplay = item.attributeDisplay;
                                const qty =
                                  parseFloat(item.quantity) % 1 === 0
                                    ? parseInt(item.quantity)
                                    : parseFloat(item.quantity);
                                return (
                                  <div key={idx} className={styles.productItem}>
                                    <span className={styles.productName}>
                                      {productName}
                                      {attrDisplay && (
                                        <span className={styles.productAttributes}>
                                          {' '}({attrDisplay})
                                        </span>
                                      )}
                                    </span>
                                    <span className={styles.productQty}>× {qty}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                          <td className={styles.actionsColumn}>
                            <div className={styles.actionButtonsCell}>
                              {isDuplicate && (
                                <button
                                  className={styles.cancelOrderButton}
                                  onClick={() => handleCancelOrder(order)}
                                  disabled={cancellingOrderId === order.id}
                                  title="Cancel as duplicate"
                                >
                                  {cancellingOrderId === order.id ? (
                                    <Loader2 size={14} className={styles.spinning} />
                                  ) : (
                                    <Ban size={14} />
                                  )}
                                  <span>Cancel</span>
                                </button>
                              )}
                              {canCancelOrder && (
                                <button
                                  className={styles.actionIconBtn}
                                  onClick={() => handleOpenCancelModal(order)}
                                  title="Cancel order"
                                >
                                  <Ban size={14} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className={styles.pagination}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className={styles.paginationButton}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className={styles.pageInfo}>
                    {t('common.pageOf', { page: currentPage, total: totalPages })}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className={styles.paginationButton}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              <Package size={48} />
              <h3>{t('packingBatch.noEligibleOrders')}</h3>
              <p>{t('packingBatch.adjustFilters')}</p>
            </div>
          )}
        </div>
      )}

      {/* Cancel Order Modal */}
      {cancelModal && (
        <div className={styles.modalOverlay} onClick={() => !isCancellingOrder && setCancelModal(null)}>
          <div className={styles.cancelModal} onClick={e => e.stopPropagation()}>
            <div className={styles.cancelModalHeader}>
              <h3>Cancel Order #{cancelModal.order.orderNumber}</h3>
              <button
                className={styles.modalCloseButton}
                onClick={() => setCancelModal(null)}
                disabled={isCancellingOrder}
              >
                <X size={18} />
              </button>
            </div>
            <div className={styles.cancelModalBody}>
              <label htmlFor="cancelReason">Select cancellation reason</label>
              <select
                id="cancelReason"
                className={styles.cancelReasonSelect}
                value={cancelModalReason}
                onChange={e => setCancelModalReason(e.target.value)}
                disabled={isCancellingOrder}
              >
                {cancelReasons.map(r => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
            </div>
            <div className={styles.cancelModalFooter}>
              <button
                className={styles.cancelModalCancelBtn}
                onClick={() => setCancelModal(null)}
                disabled={isCancellingOrder}
              >
                Keep Order
              </button>
              <button
                className={styles.cancelModalConfirmBtn}
                onClick={handleCancelOrderWithReason}
                disabled={isCancellingOrder || !cancelModalReason}
              >
                {isCancellingOrder ? <Loader2 size={14} className={styles.spinning} /> : <Ban size={14} />}
                Cancel Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Records Popup */}
      {paymentRecordsPopup && (
        <div className={styles.modalOverlay} onClick={() => setPaymentRecordsPopup(null)}>
          <div className={styles.paymentRecordsModal} onClick={e => e.stopPropagation()}>
            <div className={styles.paymentRecordsHeader}>
              <h3>
                <Receipt size={16} />
                Payment Records — #{paymentRecordsPopup.order.orderNumber}
              </h3>
              <button
                className={styles.modalCloseButton}
                onClick={() => setPaymentRecordsPopup(null)}
              >
                <X size={18} />
              </button>
            </div>
            <div className={styles.paymentRecordsBody}>
              {paymentRecordsPopup.records.length === 0 ? (
                <p className={styles.noRecordsText}>No payment records found.</p>
              ) : (
                <table className={styles.paymentRecordsTable}>
                  <thead>
                    <tr>
                      <th>Method</th>
                      <th>Amount</th>
                      <th>Reference</th>
                      <th>Date</th>
                      <th>Recorded By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentRecordsPopup.records.map(rec => (
                      <tr key={rec.id}>
                        <td>{rec.PaymentMethod?.label || '—'}</td>
                        <td>{formatCurrency(rec.paymentAmount)}</td>
                        <td>{rec.reference || '—'}</td>
                        <td>{rec.paymentDate || '—'}</td>
                        <td>{rec.createdBy?.name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Progress Modal */}
      {showProgress && (
        <div className={styles.modalOverlay}>
          <div className={styles.progressModal}>
            <div className={styles.progressHeader}>
              <h3>{t('packingBatch.creatingBatch')}</h3>
              <div className={styles.progressStats}>
                {t('packingBatch.processingOrder')} {progressData.current}{' '}
                {t('packingBatch.of')} {progressData.total}
                {progressData.courierAccount && (
                  <span> • {progressData.courierAccount}</span>
                )}
              </div>
            </div>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{
                  width: `${
                    progressData.total > 0
                      ? (progressData.current / progressData.total) * 100
                      : 0
                  }%`
                }}
              />
            </div>
            <div className={styles.progressList}>
              {progressData.results
                .slice(-5)
                .reverse()
                .map((result, index) => (
                  <div key={index} className={styles.progressItem}>
                    <div className={styles.progressOrder}>{result.orderNumber}</div>
                    <div
                      className={`${styles.progressStatus} ${
                        styles[result.status] || ''
                      }`}
                    >
                      {result.status === 'success' ? (
                        <CheckCircle size={14} />
                      ) : result.status === 'failed' ? (
                        <AlertCircle size={14} />
                      ) : (
                        <Loader2 size={14} className={styles.spinning} />
                      )}
                      {result.status}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatePackingBatchPage;